import { jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { CheckCircle2, ArrowRight, Headphones, Camera } from "lucide-react";
function AIProducts({ data }) {
  const cards = [
    {
      icon: Headphones,
      badge: "PRODUCT 01",
      title: "AI Customer Support",
      industryTitle: data.supportHeadline,
      features: data.supportFeatures,
      img: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop&auto=format",
      gradient: "linear-gradient(135deg,#6f5cf7 0%,#8b5cf6 100%)",
      glow: "rgba(111,92,247,0.25)"
    },
    {
      icon: Camera,
      badge: "PRODUCT 02",
      title: "AI Surveillance",
      industryTitle: data.surveillanceHeadline,
      features: data.surveillanceFeatures,
      img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop&auto=format",
      gradient: "linear-gradient(135deg,#1d4ed8 0%,#6f5cf7 100%)",
      glow: "rgba(29,78,216,0.2)"
    }
  ];
  return /* @__PURE__ */ jsx("section", { className: "py-24 px-6", style: { backgroundColor: "#f8fafc" }, children: /* @__PURE__ */ jsxs("div", { className: "max-w-7xl mx-auto", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-center mb-16", children: [
      /* @__PURE__ */ jsx(
        motion.div,
        {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.55 },
          className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-semibold",
          style: { backgroundColor: "rgba(111,92,247,0.07)", border: "1px solid rgba(111,92,247,0.18)", color: "#6f5cf7", fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" },
          children: "AI PRODUCTS"
        },
        data.id
      ),
      /* @__PURE__ */ jsx(
        motion.h2,
        {
          initial: { opacity: 0, y: 20 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay: 0.05 },
          style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(1.8rem,3.5vw,2.75rem)", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", marginBottom: "0.75rem" },
          children: "Two Flagship AI Products"
        },
        `h-${data.id}`
      ),
      /* @__PURE__ */ jsxs(
        motion.p,
        {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, delay: 0.1 },
          style: { fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#6b7280", maxWidth: "520px", margin: "0 auto" },
          children: [
            "Enterprise-grade AI built for ",
            /* @__PURE__ */ jsx("strong", { style: { color: data.color }, children: data.tagline.split("AI for ")[1] }),
            "."
          ]
        },
        `p-${data.id}`
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6", children: cards.map((card, i) => {
      const Icon = card.icon;
      return /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, y: 32 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.65, delay: i * 0.12 },
          whileHover: { y: -6, boxShadow: `0 24px 64px ${card.glow}` },
          className: "group relative rounded-3xl overflow-hidden border bg-white cursor-pointer transition-all duration-400",
          style: { border: "1px solid #e5e7eb", boxShadow: "0 4px 24px rgba(0,0,0,0.05)" },
          children: [
            /* @__PURE__ */ jsxs("div", { className: "relative h-52 overflow-hidden", children: [
              /* @__PURE__ */ jsx("img", { src: card.img, alt: card.title, className: "w-full h-full object-cover opacity-50 group-hover:opacity-65 group-hover:scale-105 transition-all duration-700" }),
              /* @__PURE__ */ jsx("div", { className: "absolute inset-0", style: { background: `linear-gradient(to bottom,${card.gradient.replace("linear-gradient(135deg,", "").replace("100%)", "70%)")},transparent 0%)` } }),
              /* @__PURE__ */ jsx("div", { className: "absolute inset-0", style: { background: "linear-gradient(to top,white 0%,transparent 60%)" } }),
              /* @__PURE__ */ jsxs("div", { className: "absolute top-5 left-5 flex items-center gap-3", children: [
                /* @__PURE__ */ jsx("div", { className: "w-11 h-11 rounded-2xl flex items-center justify-center", style: { background: card.gradient, boxShadow: `0 4px 16px ${card.glow}` }, children: /* @__PURE__ */ jsx(Icon, { size: 20, color: "white" }) }),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "px-3 py-1 rounded-full text-[10px] font-bold tracking-widest",
                    style: { backgroundColor: "rgba(255,255,255,0.9)", color: "#374151", fontFamily: "'Manrope',sans-serif" },
                    children: card.badge
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "p-8 pt-4", children: [
              /* @__PURE__ */ jsx("h3", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "#111827", letterSpacing: "-0.02em", marginBottom: "0.25rem" }, children: card.title }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "inline-block px-3 py-1 rounded-xl text-sm font-semibold mb-6",
                  style: { backgroundColor: `${data.color}10`, color: data.color, fontFamily: "'Manrope',sans-serif" },
                  children: card.industryTitle
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-y-2.5 gap-x-4 mb-8", children: card.features.map((f) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx(CheckCircle2, { size: 14, style: { color: "#10b981", flexShrink: 0 } }),
                /* @__PURE__ */ jsx("span", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.82rem", color: "#374151" }, children: f })
              ] }, f)) }),
              /* @__PURE__ */ jsxs(
                "button",
                {
                  className: "flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-sm transition-all duration-200 hover:scale-105",
                  style: {
                    fontFamily: "'Plus Jakarta Sans',sans-serif",
                    background: card.gradient,
                    color: "white",
                    boxShadow: `0 4px 20px ${card.glow}`
                  },
                  children: [
                    "Explore Product ",
                    /* @__PURE__ */ jsx(ArrowRight, { size: 15 })
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none",
                style: { boxShadow: `inset 0 0 0 2px ${card.glow.replace("0.25", "0.4").replace("0.2", "0.3")}` }
              }
            )
          ]
        },
        `${card.badge}-${data.id}`
      );
    }) })
  ] }) });
}
export {
  AIProducts
};
